// src/api/marketValueApi.js
// Piyasa Değeri & Haftanın Enleri API yardımcıları.
import api, { apiGet } from "./api";

const id = (value) => encodeURIComponent(String(value));
const clean = (params) =>
  Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== "" && v != null));

export const ETL = "ETL";

// Piyasa değerini ETL biçiminde formatla (ör. 3.450.000 ETL).
export const formatEtl = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Math.round(Number(value));
  return `${n.toLocaleString("tr-TR")} ${ETL}`;
};

export const WEEKLY_ITEM_TYPES = {
  WEEKLY_PLAYER: "Haftanın Oyuncusu",
  WEEKLY_GOALKEEPER: "Haftanın Kalecisi",
  WEEKLY_DEFENDER: "Haftanın Defansı",
  WEEKLY_MIDFIELDER: "Haftanın Orta Sahası",
  WEEKLY_FORWARD: "Haftanın Forveti",
  WEEKLY_FAIR_PLAY: "Haftanın Centilmeni",
  WEEKLY_TEAM: "Haftanın Takımı",
  WEEKLY_SQUAD_PLAYER: "Haftanın Kadrosu",
};

export const BREAKDOWN_LABELS = {
  generalPerformance: "Genel Performans",
  recentForm: "Son Dönem Formu",
  positionPerformance: "Pozisyona Özel",
  awards: "Ödüller",
  experience: "Tecrübe ve Süreklilik",
  discipline: "Disiplin",
  teamContribution: "Takım Katkısı",
};

// Geçmiş kayıtlarındaki hesaplama nedenleri (player_market_value_history.calculation_reason).
export const CALC_REASON_LABELS = {
  INITIAL_CALCULATION: "Başlangıç hesabı",
  MATCH_COMPLETED: "Maç tamamlandı",
  MATCH_RATING_UPDATED: "Maç puanı güncellendi",
  MATCH_AWARD_UPDATED: "Maç ödülü güncellendi",
  WEEKLY_AWARD_PUBLISHED: "Haftanın enleri yayınlandı",
  WEEKLY_AWARD_REMOVED: "Haftanın enleri geri alındı",
  DISCIPLINE_UPDATED: "Disiplin güncellendi",
  NIGHTLY_RECALCULATION: "Gece toplu hesap",
  MANUAL_RECALCULATION: "Manuel yeniden hesap",
  MANUAL_ADJUSTMENT: "Yönetici düzeltmesi",
};

// Pozisyon grupları ve istatistik anahtarlarının panel etiketleri.
export const POSITION_LABELS = {
  GOALKEEPER: "Kaleci",
  DEFENDER: "Defans",
  MIDFIELDER: "Orta Saha",
  FORWARD: "Forvet",
};

export const STAT_KEY_LABELS = {
  generalRatingScore: "Genel puan skoru",
  goalsPer90: "Gol / 90dk",
  assistsPer90: "Asist / 90dk",
  goalContributionsPer90: "Gol katkısı / 90dk",
  scoringMatchRatio: "Gollü maç oranı",
  savesPer90: "Kurtarış / 90dk",
  blocksPer90: "Kritik blok / 90dk",
  tacklesPer90: "İkili mücadele / 90dk",
  keyPassesPer90: "Yaratılan pozisyon / 90dk",
  cleanSheetRatio: "Gol yemeden bitirme oranı",
  goalsConcededPer90: "Yenilen gol / 90dk",
  penaltySaves: "Penaltı kurtarışı",
  duelWinRatio: "İkili mücadele kazanma oranı",
  shotsPer90: "Şut / 90dk",
};

export const marketValueApi = {
  // ---- Public ----
  list: async (params = {}, config = {}) =>
    (await apiGet("/api/market-values", { ...config, params: clean(params) })).data,
  rankings: async (params = {}, config = {}) =>
    (await apiGet("/api/market-values/rankings", { ...config, params: clean(params) })).data,
  player: async (playerId, config = {}) =>
    (await apiGet(`/api/players/${id(playerId)}/market-value`, config)).data,
  playerHistory: async (playerId, params = {}, config = {}) =>
    (await apiGet(`/api/players/${id(playerId)}/market-value/history`, { ...config, params: clean(params) })).data,
  playerBreakdown: async (playerId, config = {}) =>
    (await apiGet(`/api/players/${id(playerId)}/market-value/breakdown`, config)).data,

  // ---- Admin (piyasa değeri) ----
  backfillDefaultRatings: async (body) =>
    (await api.post("/api/admin/market-value/backfill-default-ratings", body)).data,
  initialize: async (body) => (await api.post("/api/admin/market-value/initialize", body)).data,
  recalculate: async (body) => (await api.post("/api/admin/market-value/recalculate", body)).data,
  recalculatePlayer: async (playerId, body = {}) =>
    (await api.post(`/api/admin/market-value/recalculate/${id(playerId)}`, body)).data,
  getSettings: async (config = {}) => (await apiGet("/api/admin/market-value/settings", config)).data,
  updateSettings: async (body) => (await api.put("/api/admin/market-value/settings", body)).data,
  updateStatWeights: async (body) => (await api.put("/api/admin/market-value/stat-weights", body)).data,
  adjustPlayerValue: async (playerId, body) =>
    (await api.post(`/api/admin/market-value/players/${id(playerId)}/adjust`, body)).data,
  auditLog: async (params = {}, config = {}) =>
    (await apiGet("/api/admin/market-value/audit-log", { ...config, params: clean(params), dedupe: false, useCache: false })).data,
  formulaVersions: async (config = {}) => (await apiGet("/api/admin/market-value/formula-versions", config)).data,
  activateFormulaVersion: async (versionId) =>
    (await api.post(`/api/admin/market-value/formula-versions/${id(versionId)}/activate`)).data,

  // ---- Public (haftanın enleri, yayınlanmış) ----
  weeklyPublic: async (params = {}, config = {}) =>
    (await apiGet("/api/weekly-awards/public", { ...config, params: clean(params) })).data,
  weeklyPublicDetail: async (setId, config = {}) =>
    (await apiGet(`/api/weekly-awards/public/${id(setId)}`, config)).data,
  // İçerik havuzu: taslak + yayınlanmış setler (giriş ve yetki ister).
  weeklyContent: async (params = {}, config = {}) =>
    (await apiGet("/api/weekly-awards/content", { ...config, params: clean(params) })).data,

  // ---- Admin (haftanın enleri) ----
  weekly: {
    list: async (params = {}, config = {}) =>
      (await apiGet("/api/admin/weekly-awards", { ...config, params: clean(params) })).data,
    detail: async (setId, config = {}) =>
      (await apiGet(`/api/admin/weekly-awards/${id(setId)}`, config)).data,
    candidates: async (params = {}, config = {}) =>
      (await apiGet("/api/admin/weekly-awards/candidates", { ...config, params: clean(params) })).data,
    players: async (params = {}, config = {}) =>
      (await apiGet("/api/admin/weekly-awards/players", { ...config, params: clean(params) })).data,
    teams: async (params = {}, config = {}) =>
      (await apiGet("/api/admin/weekly-awards/teams", { ...config, params: clean(params) })).data,
    leagues: async (params = {}, config = {}) =>
      (await apiGet("/api/admin/weekly-awards/leagues", { ...config, params: clean(params) })).data,
    seasons: async (params = {}, config = {}) =>
      (await apiGet("/api/admin/weekly-awards/seasons", { ...config, params: clean(params) })).data,
    create: async (body) => (await api.post("/api/admin/weekly-awards", body)).data,
    update: async (setId, body) => (await api.patch(`/api/admin/weekly-awards/${id(setId)}`, body)).data,
    remove: async (setId) => (await api.delete(`/api/admin/weekly-awards/${id(setId)}`)).data,
    publish: async (setId) => (await api.post(`/api/admin/weekly-awards/${id(setId)}/publish`)).data,
    // "Taslağa Al" — yayından kaldırır ve piyasa değeri etkisini geri alır.
    unpublish: async (setId) => (await api.post(`/api/admin/weekly-awards/${id(setId)}/unpublish`)).data,
    // "Yayından Al" — yayından kaldırır ama piyasa değerini korur.
    withdraw: async (setId) => (await api.post(`/api/admin/weekly-awards/${id(setId)}/withdraw`)).data,
    archive: async (setId) => (await api.post(`/api/admin/weekly-awards/${id(setId)}/archive`)).data,
  },
};

export default marketValueApi;
